import type { RefObject, LegacyRef } from 'react'
import { Link } from 'react-router-dom'
import { Link2, ChevronDown, CheckCircle, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import type { Integration } from '../external-types'

export interface IntegrationsDropdownProps {
  integrations: Integration[]
  show: boolean
  onToggle: () => void
  onRefresh: () => void
  /** Ref for the trigger button wrapper — used by the parent's click-outside handler. */
  buttonRef: RefObject<HTMLDivElement | null>
}

export function IntegrationsDropdown({
  integrations,
  show,
  onToggle,
  onRefresh,
  buttonRef,
}: IntegrationsDropdownProps) {
  const connectedIntegrations = integrations.filter((i) => i.status === 'connected')
  const connectedCount = connectedIntegrations.length

  return (
    <div className="relative" ref={buttonRef as LegacyRef<HTMLDivElement>}>
      <button
        onClick={onToggle}
        className="integrations-button inline-flex h-7 items-center gap-1.5 rounded-md border bg-card px-2.5 text-xs font-medium text-foreground/80 transition hover:bg-foreground/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
        <span>Integrations</span>
        {connectedCount > 0 && (
          <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-success-soft px-1 text-[10px] font-semibold leading-none text-success-foreground">
            {connectedCount}
          </span>
        )}
        <ChevronDown
          className={clsx('h-3.5 w-3.5 text-muted-foreground transition-transform', show && 'rotate-180')}
        />
      </button>

      {show && (
        <div className="integrations-dropdown absolute right-0 mt-2 w-96 bg-card rounded-lg shadow-lg border border-strong z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-foreground">Connected Integrations</h3>
              <button
                onClick={onRefresh}
                className="p-1 hover:bg-foreground/[0.06] rounded transition"
                title="Refresh integrations"
              >
                <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              TestOps Command can use these connected tools during chat when your request provides enough context.
            </p>

            {connectedIntegrations.length > 0 ? (
              <div className="space-y-2">
                {connectedIntegrations.map((integration) => (
                  <div
                    key={integration.connectionId || integration.name}
                    className="px-3 py-2 rounded-lg border border-success/30 bg-success-soft flex items-center justify-between"
                  >
                    <div>
                      <span className="text-sm font-medium text-foreground">
                        {integration.name}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                        {integration.provider}
                      </p>
                      {(integration.capabilities?.length ?? 0) > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Tools: {integration.capabilities?.slice(0, 3).join(', ')}
                        </p>
                      )}
                      {integration.lastSync && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Last sync: {new Date(integration.lastSync).toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                    <CheckCircle className="w-4 h-4 text-success-foreground" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center">
                <Link2 className="w-10 h-10 text-subtle-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No active connections</p>
                <p className="text-xs text-subtle-foreground mt-1">
                  Connect Jira, GitHub, and more on the{' '}
                  <Link to="/app/integrations" className="text-accent hover:text-accent underline">
                    Integrations
                  </Link>{' '}
                  page
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
